
import { useState } from 'react';

const plans = ['開業サポート', '開業＋運営設計', '運営代行'];

const categories = [
  {
    label: '申請・開業サポート',
    icon: 'ri-file-list-3-line',
    items: [
      { feature: '簡易宿所・民泊届出申請サポート', values: [true, true, false] },
      { feature: '保健所・消防対応アドバイス', values: [true, true, false] },
      { feature: '物件選定アドバイス', values: [false, true, false] },
      { feature: '初期価格戦略設計', values: [true, true, false] },
    ],
  },
  {
    label: 'セットアップ・撮影',
    icon: 'ri-camera-line',
    items: [
      { feature: 'Airbnbページ立ち上げ・最適化', values: [true, true, false] },
      { feature: 'プロ写真ディレクション', values: [false, true, false] },
      { feature: 'インテリア・レイアウト提案', values: [false, true, false] },
      { feature: '清掃体制の構築', values: [false, true, false] },
      { feature: '収益モデル設計', values: [false, true, false] },
    ],
  },
  {
    label: '日常運営代行',
    icon: 'ri-calendar-check-line',
    items: [
      { feature: '予約管理・価格調整（毎日）', values: [false, false, true] },
      { feature: 'ゲスト対応（24時間）', values: [false, false, true] },
      { feature: 'レビュー管理・返信', values: [false, false, true] },
      { feature: '清掃手配・品質管理', values: [false, false, true] },
      { feature: '月次収益レポート', values: [false, false, true] },
      { feature: '売上改善提案', values: [false, false, true] },
    ],
  },
  {
    label: 'サポート・保証',
    icon: 'ri-shield-check-line',
    items: [
      { feature: 'メール・チャットサポート', values: [true, true, true] },
      { feature: '専任担当者によるサポート', values: [false, true, true] },
      { feature: 'トラブル時の緊急対応', values: [false, false, true] },
      { feature: '経理・確定申告サポート（別途）', values: [false, true, true] },
    ],
  },
];

const priceInfo = [
  { price: '¥300,000〜', note: '¥500,000', color: 'text-slate-700' },
  { price: '¥600,000〜', note: '¥1,000,000', color: 'text-emerald-600' },
  { price: '15〜20%', note: '月間売上より', color: 'text-slate-700' },
];

export default function ServiceComparisonSection() {
  const [openCategory, setOpenCategory] = useState<number | null>(null);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-full mb-4">
            サービス比較
          </span>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            各プランの機能比較
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            目的やフェーズに合わせて最適なプランをお選びください
          </p>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              {/* Sticky Header */}
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="px-8 py-6 text-left w-2/5 bg-slate-50">
                    <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">機能・サービス</span>
                  </th>
                  {plans.map((plan, i) => (
                    <th
                      key={i}
                      className={`px-6 py-6 text-center w-1/5 ${
                        i === 1 ? 'bg-emerald-600' : 'bg-slate-50'
                      }`}
                    >
                      <div className={`text-base font-bold mb-1 ${i === 1 ? 'text-white' : 'text-slate-800'}`}>
                        {plan}
                      </div>
                      <div className={`text-xs font-semibold ${i === 1 ? 'text-emerald-100' : priceInfo[i].color}`}>
                        {priceInfo[i].price}
                      </div>
                      <div className={`text-xs ${i === 1 ? 'text-emerald-200' : 'text-slate-400'}`}>
                        {priceInfo[i].note}
                      </div>
                      {i === 1 && (
                        <span className="inline-block mt-2 px-3 py-0.5 bg-amber-400 text-slate-900 text-xs font-bold rounded-full whitespace-nowrap">
                          人気No.1
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {categories.map((cat, catIdx) => (
                  <>
                    {/* Category Row */}
                    <tr
                      key={`cat-${catIdx}`}
                      className="bg-slate-50 cursor-pointer select-none"
                      onClick={() =>
                        setOpenCategory(openCategory === catIdx ? null : catIdx)
                      }
                    >
                      <td colSpan={4} className="px-8 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg">
                            <i className={`${cat.icon} text-emerald-600 text-base`}></i>
                          </div>
                          <span className="text-sm font-bold text-slate-700">{cat.label}</span>
                          <i
                            className={`ri-arrow-down-s-line text-slate-400 text-lg ml-auto transition-transform duration-300 ${
                              openCategory === catIdx ? 'rotate-180' : ''
                            }`}
                          ></i>
                        </div>
                      </td>
                    </tr>

                    {/* Feature Rows */}
                    {(openCategory === catIdx || openCategory === null) &&
                      cat.items.map((item, itemIdx) => (
                        <tr
                          key={`item-${catIdx}-${itemIdx}`}
                          className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
                        >
                          <td className="px-8 py-4">
                            <span className="text-sm text-slate-700 leading-relaxed">
                              {item.feature}
                            </span>
                          </td>
                          {item.values.map((val, vi) => (
                            <td
                              key={vi}
                              className={`px-6 py-4 text-center ${
                                vi === 1 ? 'bg-emerald-50/40' : ''
                              }`}
                            >
                              {val ? (
                                <div className="flex justify-center">
                                  <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-full">
                                    <i className="ri-check-line text-emerald-600 text-base font-bold"></i>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-center">
                                  <div className="w-7 h-7 flex items-center justify-center bg-slate-100 rounded-full">
                                    <i className="ri-subtract-line text-slate-300 text-base"></i>
                                  </div>
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </>
                ))}
              </tbody>

              {/* Footer CTA Row */}
              <tfoot>
                <tr className="border-t-2 border-slate-100 bg-slate-50">
                  <td className="px-8 py-6">
                    <p className="text-sm text-slate-500">
                      ご不明な点はお気軽にご相談ください
                    </p>
                  </td>
                  {plans.map((plan, i) => (
                    <td key={i} className={`px-6 py-6 text-center ${i === 1 ? 'bg-emerald-50/40' : ''}`}>
                      <a
                        href="/for-owners#contact"
                        className={`inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          i === 1
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md hover:shadow-lg'
                            : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-emerald-400 hover:text-emerald-600'
                        }`}
                      >
                        <i className="ri-calendar-line text-base"></i>
                        相談する
                      </a>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Note */}
        <p className="text-center text-sm text-slate-400 mt-6">
          ※ 各プランの詳細や組み合わせについては、無料相談にてご確認いただけます
        </p>
      </div>
    </section>
  );
}
